import { useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import type { AlertsFilterOptions } from "types/alerts.types"

import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsStates from "helpers/hooks/useAlertSettingsStates"
import useTower from "helpers/hooks/towers/useTower"
import {
  formatTowerAlerts,
  getAlertAmounts,
  getFilteredSortedAlerts,
  sortAndGroupAlerts,
} from "helpers/formatters/alertSettingsFormatters"
import Spinner from "widgets/common/Spinner"
import { SectionContainer } from "widgets/styled/containers"
import AlertsFilter from "widgets/alerts/AlertsFilter"
import AccordionsSection from "widgets/alerts/AccordionsSection"

const TowerAlertsContent = () => {
  const { id } = useParams()
  const { orgTowersAlertSettingsById, isLoading: settingsLoading } = useAlertSettings()
  const { getTowersAlertSettingsWStatesByEntityId, isLoading: settingsStatesLoading } =
    useAlertSettingsStates()
  const { towerById, isLoading: towerLoading } = useTower({ id: Number(id) })
  const [filters, setFilters] = useState<AlertsFilterOptions | null>(null)

  const towerSettingsStates = useMemo(
    () => getTowersAlertSettingsWStatesByEntityId(Number(id)),
    [getTowersAlertSettingsWStatesByEntityId, id],
  )

  const formattedTowerAlerts = useMemo(
    () => formatTowerAlerts(towerSettingsStates, orgTowersAlertSettingsById, towerById),
    [towerSettingsStates, orgTowersAlertSettingsById, towerById],
  )
  const sortedAndGroupedAlerts = useMemo(
    () => sortAndGroupAlerts(formattedTowerAlerts),
    [formattedTowerAlerts],
  )
  const towerAlertsAmount = useMemo(
    () => getAlertAmounts(sortedAndGroupedAlerts),
    [sortedAndGroupedAlerts],
  )
  const towerAlertsSortedByState = useMemo(
    () => getFilteredSortedAlerts(sortedAndGroupedAlerts, filters),
    [sortedAndGroupedAlerts, filters],
  )

  const isLoading = useMemo(
    () => settingsStatesLoading || settingsLoading || towerLoading,
    [towerLoading, settingsLoading, settingsStatesLoading],
  )

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <SectionContainer disableGutters maxWidth="xl">
          <AlertsFilter
            alertsAmount={towerAlertsAmount}
            filters={filters}
            setFilters={setFilters}
            withDisabled
          />
          <AccordionsSection
            alerts={towerAlertsSortedByState}
            filters={filters}
            entityPage
          />
        </SectionContainer>
      )}
    </>
  )
}

export default TowerAlertsContent
